/**
 * 確認の画面を「ふつうの言葉」で言うための決まり（純関数・import は pendingAction.ts / launchHidden.ts だけ = node --test でそのまま動く）。
 * 2026-10-03 console/plain-confirm。
 *
 * WHY（founder 2026-10-03・本番の /confirm を見て）: 「こんなの一般のユーザーだったら、まじでいみわからんよね。何を書いてるのか。
 *   今何しているかわかりずらすぎる」。以前の画面は「本番（利用者がいる環境）で次の 2 件の変更をこの順に頼んでいます」
 *   「公開（hosting）を ON」「バージョン 4 を本番に公開」「10 分以内に 1 回ずつ送れる」を全部、ボタンより上に並べていた。
 *   = 中の仕組み（環境・版・束・承認の仕組み）を見せていて、「押すと何が起きるか」が埋もれていた。
 *
 * いまの形（どの確認の画面も同じ並び）:
 *   1. 見出し 1 文 = 起きることを利用者の言葉で（「『ねこジャンプ』を公開しますか？」）
 *   2. 結果 1 行 = 目に見える結果（「https://… で、すぐに誰でも使えるようになります。」= 本物の URL）
 *      言葉は「アプリ / 利用者」（「ゲーム / プレイヤー」は使わない = i18n/wording.test.ts の決まり・MASTER-PLAN §0）
 *   3. 大きなボタン 2 つ（する / やめる）
 *   4. 安全の 1 行（「AI に頼んだ覚えがなければ『やめる』を押してください。」）
 *   5. ログイン中のアカウント（小さく）
 *   6. 「くわしく見る」（閉じた <details>）= 機能のキー・版の番号・環境・束の順・10 分・承認の仕組み・頼んだ AI の名前・SQL など
 * 見える所（1〜5）には中の言葉（hosting・環境・版・束・エージェントの要求…）を出さない = PLAIN_FORBIDDEN（テストが辞書と画面を確かめる）。
 *
 * 守りは変えない: ボタン・送る中身（approveBody）・取り消し（deny）・アカウントの表示・メール未確認の扱いは ConfirmAction.tsx のまま。
 * 「くわしく見る」の中身は以前の画面の全部（describeAction の文・補足・束の行）= 隠すのではなく、畳むだけ。
 * 束（初めての公開 = 公開を ON + 公開）は 1 つの結果として言う（「公開しますか？」+「いっしょに、セーブも使えるようになります」）。
 * ボタンの名前（confirmAction.button.*）は MCP の APPROVE_LABEL と同じ契約（pendingAction.ts BUTTON_OF / buttonOf・2 回目で場面ごとの名前にした）。
 *
 * 2 回目（2026-10-04 console/plain-confirm-2・founder「非技術者にとっては意味がわからない」「日本語が回りくどい」）:
 *   - 文言をふつうの日本のアプリの言い方に（i18n/ja.confirmPlain.ts）。「くわしく見る」→「詳細」
 *   - データの書き換え（db_query）の結果の 1 行は SQL を読んで具体的に言う（sqlEffect.ts）。以前は「何を実行するかは『くわしく見る』で
 *     確かめてください」= SQL を読めない人に判断を投げていた。読めない SQL は「この画面では判断できません。AI に聞いて」と正直に言う
 *   - データの保存形式の変更（db_migrate）は SQL があれば DROP の有無で言い分ける。本体（server pendingActionKinds.ts）は今 migration の
 *     名前しか渡さない = 今は「判断できません」の文になる（SQL を details に足すのは Core / Compute への申し送り）
 */

import { isLaunchHidden } from './launchHidden.ts';
import { sqlEffect } from './sqlEffect.ts';
import type { PendingActionResponse, PendingActionView, PendingStatus } from './pendingAction.ts';

/** 見える所に出してはいけない「中の言葉」（en は語の境目で・大文字小文字を問わない / ja は部分一致）。テストが辞書と画面を確かめる */
export const PLAIN_FORBIDDEN = {
  // 2 回目（2026-10-04）で足した物: program / prepaid / appeal / secret value（ja の「プログラム」「前払い残高」「異議」「秘密の値」と同じ）
  en: ['hosting', 'environment', 'production', 'version', 'agent', 'request', 'bundle', 'config', 'deploy', 'migration', 'binding', 'SQL', 'MCP', 'CLI', 'scope', 'token', 'endpoint', 'API', 'program', 'prepaid', 'appeal', 'secret value'],
  // 「反映する」はボタンの名前としてだけ禁止（結果の文の「すぐに反映されます」は ふつうの言い方）= confirmPlain.test.ts がボタンだけ別に確かめる
  ja: ['hosting', '本番', '環境', 'バージョン', '版', 'エージェント', '要求', '束', '設定を反映', 'デプロイ', 'マイグレーション', 'バインディング', 'SQL', 'MCP', 'CLI', 'スコープ', 'トークン', 'API', 'プログラム', 'BAN', '異議', '秘密の値', '前払い', 'くわしく見る']
} as const;

/** 文字列が中の言葉を含むか（含んだ言葉を返す・無ければ null） */
export function forbiddenWordIn(s: string, lang: 'en' | 'ja'): string | null {
  // 差し込み（{name} 等）は値が入る場所 = 言葉ではない。{product} は製品名
  const body = s.replace(/\{[a-zA-Z]+\}/g, ' ');
  for (const w of PLAIN_FORBIDDEN[lang]) {
    const hit = lang === 'en' ? new RegExp(`\\b${w}s?\\b`, 'i').test(body) : body.includes(w);
    if (hit) return w;
  }
  return null;
}

/**
 * 束の中で「公開と一緒に ON にする機能」を利用者の言葉で言える物（confirmPlain.feature.<key>）。
 * ここに無い機能は名前を出さず「ほかの機能も」とまとめる（名前は「くわしく見る」の中）。隠す機能は最初から落とす（launchHidden）
 */
export const PLAIN_FEATURES: readonly string[] = ['saves', 'stats', 'multiplayer', 'emailLogin', 'googleLogin', 'discordLogin', 'appleLogin', 'xLogin', 'realtimeChannels', 'voice', 'p2p'];

export interface PlainMsg {
  key: string;
  vars: Record<string, string>;
  /** 並べて差し込む値（{tables} = 表の名前）。画面が言語ごとのつなぎ（confirmPlain.tableJoin）でつなぐ */
  lists?: Record<string, string[]>;
}

/** 画面の上に出す 2 行（見出し・結果）と、結果に続ける「一緒に使えるようになる物」 */
export interface PlainSummary {
  headline: PlainMsg;
  result: PlainMsg;
  /** 一緒に ON にする機能（ふつうの言葉で言える物のキー。言えない物があれば 'other' を最後に 1 つ） */
  alsoOn: string[];
  /**
   * 公開と同じ 1 回の確認でいっしょにすること（2026-10-08 core/one-confirm）。束の順に、言えるキー（confirmPlain.alsoDo.<key>）だけ:
   *   serverOn = サーバーを動かす（functions.enable）・serverUpdate = サーバーを更新（functions.deploy）・settings = 設定の変更（見える変化のある config.push）・
   *   dataShape = データの保存形式の変更（functions.db_migrate・2026-10-15 cli/one-confirm）・
   *   domain = 独自のアドレスを足す（hosting / functions.domain_add）。
   * WHY: founder（2026-10-08）の更新は「設定・サーバー ON・サーバーのコード・ゲーム」の 4 回の確認だった → 1 回にまとめた。まとめた確認の見える所が
   *   「公開しますか？」だけだと、サーバーの変更を見ずに押すことになる = 何が変わるかを 1 行で並べる（中身の全部は「詳細」の束の行）
   */
  alsoDo: string[];
  /**
   * 公開の束の中のデータの保存形式の変更（functions.db_migrate）で、保存済みのデータが消える・読めない時の 1 行（結果の行の前に赤で）。無ければ null。
   * WHY（2026-10-15 cli/one-confirm・founder「1 回で済まない？」→「その形でいいよ」）: 本体が migrate を公開の束に入れられるようにした。束の見える所は
   *   「公開しますか？」+ alsoDo の 1 行だけ = 単発の migrate の画面が言っていた「〜は中身ごと消えます」が見えなくなる。消える物がある時だけは
   *   束でも同じ文を赤で出す（stopsGame と同じ置き場所）= 1 回にまとめても、見ずに押させない。消えない（表を作るだけ）は alsoDo の dataShape だけ
   */
  dataWarning?: PlainMsg | null;
  /** 公開中のゲームを止める（config.push の allowTurnOff）= 結果の行の前に赤で 1 行 */
  stopsGame: boolean;
  /** 結果の行のあとに「ほかの変更もあります。」（SQL にいちばん強い物以外の変更もある時・sqlEffect の more） */
  more: boolean;
  /**
   * 公開の費用の目安（2026-10-04 money/publish-cost）。結果の行の下に小さく 1 行「費用の目安: 保存は月 $0.002・1 万回開かれると $0.18」。
   * 本体の details.costEstimate（server money/publishCost.ts・単価は請求と同じ単価表）を表示用の文字にした物。無い・読めない = null（行を出さない）
   * 行の後ろの「Prices are at cost, including the tax our providers add.」はいつも出す決まった文（2026-10-07 追記 7）= ここには持たない（ConfirmAction.tsx）
   */
  cost: { storage: string; per10k: string } | null;
  /**
   * テスト用（development）の確認か（公開を除く）。結果の行のあとに「テスト用のアプリだけ・公開中のアプリは変わらない」の 1 行（confirmPlain.testOnly）。
   * WHY（2026-10-08 core/dev-confirm-links・founder の B）: 本体が development の消す系（Functions の削除・独自ドメインを外す）・BAN なども
   *   確認のリンクで受けるようになった。見出しは本番と同じ文（「サーバーを削除しますか？」）なので、どちらのアプリかを言わないと本番を消すと読める。
   *   テスト用への公開は見出しが既に「テスト用に公開」と言う = false（同じことを 2 回言わない）
   */
  testApp: boolean;
  /**
   * 公開の確認の「いま公開中のものと比べて何が変わるか」（2026-10-25 security/prompt-lava-fixes の D・点検 docs/security/2026-10-25-prompt-lava-review.md 2-⑥）。
   * 本体の details.changes（server lib/pendingActionKinds.ts hostingChanges）。公開でない・古い本体 = null（行を出さない）。
   * names は見える所に出す分だけ（種類ごと VISIBLE_NAMES 件）。全部（本体の上限 20 件）は「詳細」の中（pendingAction.ts describeAction）。
   * WHY 見える所か: 以前の画面は「公開しますか？」と URL だけ = 中の指示で入った悪いページ・ファイルを、人は「いつもの更新」と見分けられなかった
   */
  files: { first: boolean; added: number; changed: number; removed: number; names: { added: string[]; changed: string[]; removed: string[] } } | null;
  /**
   * 本番の公開で、テスト用（development）にいま公開中のものと同じファイルか（本体の details.sameAsTest）。true / false / null（比べられない）。
   * 画面はテスト用の URL（本体の GET …/development/hosting）と一緒に出す = 押す前に開いて試せる。false は「テスト用で試した物と違う」の注意
   */
  sameAsTest: boolean | null;
  /**
   * 新しいサーバーのコードに書かれた外のアドレス（本体の functions.deploy の details.outboundHosts・束の中の分も）。無い・古い本体 = null。
   * more = 画面に出さなかった数（本体の数 outboundHostCount − 出した数）。WHY: データを外へ送るコードが足されても、以前の画面は
   * 「サーバーの更新」としか言わなかった = 知らないアドレスに気づけるように並べる（D）
   */
  hosts: { names: string[]; more: number } | null;
}

/** 見える所に並べるファイルの名前の数（種類ごと）。スマホの幅で 2〜3 行に収まる数 */
export const VISIBLE_NAMES = 5;

/** 本体の details.changes → 表示の形（数が数でない・負 = null = 行を出さない = 知らないことを「変わらない」と言わない） */
export function filesOf(details: Record<string, unknown>): PlainSummary['files'] {
  const c = details.changes as Record<string, unknown> | null | undefined;
  if (!c || typeof c !== 'object') return null;
  const n = (v: unknown) => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null);
  const added = n(c.added);
  const changed = n(c.changed);
  const removed = n(c.removed);
  if (added === null || changed === null || removed === null) return null;
  const list = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0).map((x) => x.slice(0, 200)).slice(0, VISIBLE_NAMES) : []);
  return { first: c.firstPublish === true, added, changed, removed, names: { added: list(c.addedFiles), changed: list(c.changedFiles), removed: list(c.removedFiles) } };
}

/** 束（か 1 件）の中のサーバーの更新の、コードに書かれた外のアドレス（PlainSummary.hosts）。無ければ null */
export function hostsOf(all: ReadonlyArray<Pick<PendingActionView, 'summary' | 'kind'>>): PlainSummary['hosts'] {
  const names: string[] = [];
  let total = 0;
  let seen = false;
  for (const a of all) {
    if (kindOf(a) !== 'functions.deploy') continue;
    const d = detailsOf(a);
    if (!Array.isArray(d.outboundHosts)) continue;
    seen = true;
    const hs = d.outboundHosts.filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length <= 253);
    for (const h of hs) if (!names.includes(h)) names.push(h);
    total += typeof d.outboundHostCount === 'number' && d.outboundHostCount >= hs.length ? d.outboundHostCount : hs.length;
  }
  if (!seen || names.length === 0) return null;
  return { names, more: Math.max(0, total - names.length) };
}

/**
 * ドルの目安を短い文字に（有効数字 2 桁・**切り上げ** = 目安は上限側に倒す・本体も上限側の前提で数えている）。
 *   0 → "$0"・0.0021 → "$0.0021"・0.002 → "$0.002"・0.18 → "$0.18"・0.5 → "$0.50"・12.341 → "$12.35"
 * WHY 有効数字か（セントに丸めないか）: 1 万回で $0.002 のような小さな額は $0.01 にすると 5 倍に見える = founder の「具体的な数字で」に反する。
 * 同じ決まりを CLI（packages/cli/src/lib/costEstimate.ts）と MCP（apps/mcp/src/costEstimate.ts）も持つ（パッケージが別 = 3 か所。変えるときは揃える）
 */
export function usdText(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '$0';
  const decimals = n >= 1 ? 2 : Math.max(2, 1 - Math.floor(Math.log10(n)));
  const f = 10 ** decimals;
  // toPrecision: 0.18 × 100 = 18.000000000000004 を 19 に切り上げない
  let s = (Math.ceil(Number((n * f).toPrecision(12))) / f).toFixed(decimals);
  while (s.length - s.indexOf('.') - 1 > 2 && s.endsWith('0')) s = s.slice(0, -1);
  return `$${s}`;
}

/** 本体の details.costEstimate → 表示の 2 つ（数でない・負の値 = null = 行を出さない） */
export function costOf(details: Record<string, unknown>): PlainSummary['cost'] {
  const c = details.costEstimate as { storagePerMonthUsd?: unknown; per10kPlaysUsd?: unknown } | null | undefined;
  const ok = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;
  if (!c || typeof c !== 'object' || !ok(c.storagePerMonthUsd) || !ok(c.per10kPlaysUsd)) return null;
  return { storage: usdText(c.storagePerMonthUsd), per10k: usdText(c.per10kPlaysUsd) };
}

const kindOf = (v: Pick<PendingActionView, 'summary' | 'kind'>) => v.summary?.kind ?? v.kind ?? '';
const detailsOf = (v: Pick<PendingActionView, 'summary'>) => (v.summary?.details ?? {}) as Record<string, unknown>;
const names = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0 && !isLaunchHidden(x)) : []);
const str = (v: unknown, max = 200) => (typeof v === 'string' ? v.trim().slice(0, max) : typeof v === 'number' && Number.isFinite(v) ? String(v) : '');

/** 本体の hosting の url（公開先）を画面に出せる形か。http(s) の URL だけ（それ以外は null = URL なしの文） */
export function liveUrlOf(v: unknown): string | null {
  if (typeof v !== 'string' || v.length > 300) return null;
  try {
    const u = new URL(v);
    return u.protocol === 'https:' || u.protocol === 'http:' ? v : null;
  } catch {
    return null;
  }
}

/** 一緒に ON にする機能 → ふつうの言葉で言えるキー + 言えない物があれば 'other' */
export function plainFeatures(on: readonly string[]): string[] {
  const rest = on.filter((n) => n !== 'hosting');
  const known = rest.filter((n) => PLAIN_FEATURES.includes(n));
  return known.length < rest.length ? [...known, 'other'] : known;
}

/**
 * 見出しと結果（kind ごと）。{name} = プロジェクトの名前（無ければ「あなたのゲーム」の文に替える = vars に name を入れない）、
 * {url} = 公開先（ConfirmAction.tsx が本体の hosting から読んだ本物の URL。無ければ URL なしの文）。
 * 束は「いちばん大きな結果」で言う: 公開（hosting.activate）を含めば「公開しますか？」+ 一緒に ON にする機能。
 * それ以外の束は最後の操作の文 + 「ほかに N 件」（くわしく見る）。
 */
export function plainSummary(data: PendingActionResponse, opts: { projectName?: string | null; liveUrl?: string | null } = {}): PlainSummary {
  const all: Array<Pick<PendingActionView, 'summary' | 'kind'>> = Array.isArray(data.bundle?.actions) && data.bundle!.actions.length >= 2
    ? [...data.bundle!.actions].sort((a, b) => a.position - b.position)
    : [data.action];
  const name = str(opts.projectName, 80);
  const url = str(opts.liveUrl, 300);
  const n: Record<string, string> = name ? { name } : {};
  const sfx = name ? '' : '_noName';
  const msg = (k: string, vars: Record<string, string> = {}): PlainMsg => ({ key: `confirmPlain.${k}`, vars });

  // 公開中のゲームを止める（AGT-16）・一緒に ON にする機能は束の全部から集める
  let stopsGame = false;
  const on: string[] = [];
  for (const a of all) {
    if (kindOf(a) !== 'config.push') continue;
    const d = detailsOf(a);
    if (names(d.allowTurnOff).length) stopsGame = true;
    on.push(...names(d.featuresOn));
  }

  const publish = all.find(isPublish);
  if (publish) {
    const also = plainFeatures(on);
    const alsoDo: string[] = [];
    // 束の migrate で、データが残ると言えない物（消える・読めない）の最初の 1 本の文（PlainSummary.dataWarning の WHY）
    let dataWarning: PlainMsg | null = null;
    for (const a of all) {
      const k = alsoDoOf(a);
      if (k && !alsoDo.includes(k)) alsoDo.push(k);
      if (kindOf(a) === 'functions.db_migrate' && !dataWarning) {
        const line = migrateLine(detailsOf(a).sql, msg);
        if (line.key !== 'confirmPlain.result.dataShapeKeep') dataWarning = line;
      }
    }
    // テスト用（development）への公開の確認（今の本体は本番だけ確認を出すが、出たときに「誰でも開ける」と言わない・2026-10-08 publish-url）
    const p = publishTarget(data) === 'test' ? 'publishTest' : 'publish';
    return {
      headline: msg(`headline.${p}${sfx}`, n),
      result: url ? msg(`result.${p}`, { url }) : msg(`result.${p}_noUrl`),
      alsoOn: also,
      alsoDo,
      stopsGame,
      more: false,
      cost: costOf(detailsOf(publish)),
      testApp: false,
      dataWarning,
      files: filesOf(detailsOf(publish)),
      sameAsTest: typeof detailsOf(publish).sameAsTest === 'boolean' ? (detailsOf(publish).sameAsTest as boolean) : null,
      hosts: hostsOf(all)
    };
  }
  const last = all[all.length - 1]!;
  const one = single(last, n, sfx, url, msg);
  // 公開を伴わない束（今は無いが、本体が増やしたとき）: 最後の操作の文 + ほかに N 件
  if (all.length >= 2) one.result = msg('result.andMore', { ...one.result.vars, count: String(all.length - 1) });
  return {
    headline: one.headline,
    result: one.result,
    alsoOn: [],
    alsoDo: [],
    stopsGame,
    more: one.more === true,
    cost: null,
    testApp: data.action.environment === 'development',
    files: null,
    sameAsTest: null,
    hosts: hostsOf(all)
  };
}

/**
 * 公開の束の中の 1 本 → alsoDo のキー（PlainSummary.alsoDo）。公開そのもの・機能を ON にするだけの反映（alsoOn が言う）は null。
 * config.push は見える節の変化か OFF にする機能があるときだけ「設定の変更」（single の config.push と同じ判断）
 */
function alsoDoOf(a: Pick<PendingActionView, 'summary' | 'kind'>): string | null {
  switch (kindOf(a)) {
    case 'functions.enable':
      return 'serverOn';
    case 'functions.deploy':
      return 'serverUpdate';
    // 2026-10-15 cli/one-confirm: 本体が migrate を公開の束に入れられるようにした（消える物は dataWarning が赤で別に言う）
    case 'functions.db_migrate':
      return 'dataShape';
    case 'hosting.domain_add':
    case 'functions.domain_add':
      return 'domain';
    case 'config.push': {
      const d = detailsOf(a);
      const sections = names(d.changedSections).filter((x) => x !== 'features');
      return sections.length || names(d.featuresOff).length ? 'settings' : null;
    }
    default:
      return null;
  }
}

const isPublish = (a: Pick<PendingActionView, 'summary' | 'kind'>) => kindOf(a) === 'hosting.activate' || kindOf(a) === 'hosting.finalize_activate';

/**
 * この確認が「アプリを公開する」確認か・どこへか（2026-10-08 console/publish-url）。
 * WHY（founder 2026-10-08「テストでも本番でも、アプリを公開したときに URL 出してあげると親切かも」）: 押した後は
 *   「許可しました。AI が続けます」だけで、どこで開けるのかが分からなかった。公開の確認なら押した後に開けるリンクを出す。
 * 'live' = 本番（誰でも開ける方）/ 'test' = テスト用（development）/ null = 公開ではない（行き先のリンクを出さない）。
 *   公開の準備（hosting を ON にするだけの config.push）は版がまだ出ない = 開いても空 → null（押す前の文の URL だけ）。
 * 環境は本体の action.environment（束の全部は同じ環境 = 本体が束を環境ごとに作る）。知らない値は本番として扱う（今の確認は本番だけ）
 */
export function publishTarget(data: PendingActionResponse): 'live' | 'test' | null {
  const all = Array.isArray(data.bundle?.actions) && data.bundle!.actions.length >= 2 ? data.bundle!.actions : [data.action];
  if (!all.some(isPublish)) return null;
  return data.action.environment === 'development' ? 'test' : 'live';
}

/**
 * 押した後（approved）・もう一度開いた（used = もう実行された）ときの 1 文。{url} は画面が新しいタブで開くリンクにする。
 * URL が無い（本体から読めない・まだ読み中）・公開でない・ほかの状態 = null（今までの confirmAction.status.* の文のまま）。
 * approved は「まもなく」: 承認は送り直しを 1 回通す印で、公開そのものは AI の送り直しで起きる（ConfirmAction.tsx の WHY）
 * 2026-10-15 console/confirm-publishing: 確認のページは使わなくなった（押した後は confirmPublishing.ts publishingView）。文のキーと形は残す（テストが決まりを守る）
 */
export function doneLine(status: PendingStatus, target: 'live' | 'test' | null, url: string | null): PlainMsg | null {
  if (!target || !url) return null;
  const test = target === 'test' ? 'Test' : '';
  if (status === 'approved') return { key: `confirmPlain.done.publish${test}`, vars: { url } };
  if (status === 'used') return { key: `confirmPlain.done.published${test}`, vars: { url } };
  return null;
}

/**
 * 公開の確認の「いま公開されているか」の 1 行（2026-10-13 console/confirm-wait）。ページの一番上（見出しのすぐ下・ボタンより上）に一番目立つ形で出す。
 * WHY（Studio の報告 2026-10-13）: `kumodeck deploy --env production` で確認のページが開いた時点で、founder が「終わった」と報告した
 *   （まだボタンを押しておらず、公開されていなかった）。ページの見出しは「公開しますか？」・結果の行は「〜で誰でも開けるようになります」で、
 *   「押すまで公開されない」はどこにも書いていなかった = 押す前・押した後・切れた後で「公開されたか」をこの 1 行で言い切る。
 * 状態ごと（tone = 見た目の色。'waiting' = まだ・'done' = 済んだ・'stopped' = 公開されないまま終わった）:
 *   pending  → まだ公開されていない。下のボタンを押すと公開される（テスト用はテスト用と言う）
 *   approved → まだ公開されていない（承認は送り直しを 1 回通す印 = 公開は送り直しで起きる）。CLI は自分で続ける = まもなく / AI の接続は AI が続けると
 *   used     → 公開された
 *   denied / expired / stale / revoked → 公開されていない（理由は下の今までの文）
 * 公開でない確認（target null）= null（「公開」の言葉が合わない。今までの画面のまま）
 */
export function publishStateLine(status: PendingStatus, target: 'live' | 'test' | null, cli: boolean): { key: string; tone: 'waiting' | 'done' | 'stopped' } | null {
  if (!target) return null;
  const test = target === 'test' ? 'Test' : '';
  if (status === 'pending') return { key: `confirmPlain.state.notYet${test}`, tone: 'waiting' };
  if (status === 'approved') return { key: cli ? 'confirmPlain.state.approvedCli' : 'confirmPlain.state.approvedChat', tone: 'waiting' };
  if (status === 'used') return { key: `confirmPlain.state.published${test}`, tone: 'done' };
  if (status === 'expired') return { key: 'confirmPlain.state.expired', tone: 'stopped' };
  if (status === 'denied') return { key: 'confirmPlain.state.cancelled', tone: 'stopped' };
  return { key: 'confirmPlain.state.notPublished', tone: 'stopped' };
}

function single(
  v: Pick<PendingActionView, 'summary' | 'kind'>,
  n: Record<string, string>,
  sfx: string,
  url: string,
  msg: (k: string, vars?: Record<string, string>) => PlainMsg
): { headline: PlainMsg; result: PlainMsg; more?: boolean } {
  const d = detailsOf(v);
  const h = (k: string, vars: Record<string, string> = {}) => msg(`headline.${k}${sfx}`, { ...n, ...vars });
  const r = (k: string, vars: Record<string, string> = {}) => msg(`result.${k}`, vars);
  switch (kindOf(v)) {
    case 'hosting.domain_add':
      return { headline: h('domainAdd', { hostname: str(d.hostname, 253) }), result: r('domainAdd') };
    case 'hosting.domain_remove':
      return { headline: h('domainRemove', { hostname: str(d.hostname, 253) }), result: r('domainRemove') };
    // サーバーの独自ドメインも見出しは同じ（利用者から見ると「そのアドレスで開ける」だけ・2 回目で揃えた）。外した後に他人が取れるのはサーバーだけ
    case 'functions.domain_add':
      return { headline: h('domainAdd', { hostname: str(d.hostname, 253) }), result: r('domainAdd') };
    case 'functions.domain_remove':
      return { headline: h('domainRemove', { hostname: str(d.hostname, 253) }), result: r('serverDomainRemove') };
    case 'config.push': {
      const on = names(d.featuresOn);
      if (names(d.allowTurnOff).length) return { headline: h('stop'), result: url ? r('stop', { url }) : r('stop_noUrl') };
      if (on.includes('hosting')) return { headline: h('allowPublish'), result: url ? r('allowPublish', { url }) : r('allowPublish_noUrl') };
      if (on.length) return { headline: h('turnOn'), result: r('turnOn') };
      // 見える変化が無い push（隠す節だけ・describeAction の config_push_invisible と同じ判断）は「見た目は変わりません」
      const sections = Array.isArray(d.changedSections) ? d.changedSections : null;
      const visible = sections ? names(sections).length > 0 || names(d.featuresOff).length > 0 : true;
      return { headline: h('settings'), result: r(visible ? 'settings' : 'settingsInvisible') };
    }
    case 'players.ban':
      return { headline: msg('headline.ban', { playerId: str(d.playerId, 80) }), result: r('ban') };
    case 'players.unban':
      return { headline: msg('headline.unban', { playerId: str(d.playerId, 80) }), result: r('unban') };
    case 'appeals.resolve':
      return d.accept === true ? { headline: msg('headline.appealAccept'), result: r('appealAccept') } : { headline: msg('headline.appealReject'), result: r('appealReject') };
    case 'functions.enable':
      return { headline: h('serverOn'), result: r('serverOn') };
    case 'functions.disable':
      return { headline: h('serverOff'), result: r('serverOff') };
    case 'functions.delete':
      // 意味は本体の details.purge だけから（describeAction と同じ・REV-02）。不正な値は「実行されません」
      if (d.purge === true) return { headline: h('serverPurge'), result: r('serverPurge') };
      if (d.purge === false) return { headline: h('serverDelete'), result: r('serverDelete') };
      return { headline: h('serverDelete'), result: r('serverDeleteInvalid') };
    case 'functions.deploy':
      return { headline: h('serverUpdate'), result: r('serverUpdate') };
    case 'functions.secret_put':
      return { headline: msg('headline.secretPut', { secret: str(d.name, 120) }), result: r('secretPut') };
    case 'functions.secret_delete':
      return { headline: msg('headline.secretDelete', { secret: str(d.name, 120) }), result: r('secretDelete') };
    case 'functions.db_query':
      // SQL を読んで具体的に言う（sqlEffect.ts・上の WHY）。SQL そのものは「詳細」の中に全部ある
      return sqlLine(d.sql, h, msg);
    case 'functions.db_migrate':
      return { headline: h('dataShape'), result: migrateLine(d.sql, msg) };
    case 'mail.schedule':
      return { headline: msg(d.recurrence ? 'headline.mailRepeat' : 'headline.mailSchedule', { subject: str(d.subject, 200) }), result: r(d.recurrence ? 'mailRepeat' : 'mailSchedule') };
    case 'mail.send':
      return { headline: msg('headline.mailSend', { subject: str(d.subject, 200) || str(d.broadcastId, 80) }), result: r('mailSend') };
    default:
      return { headline: h('other'), result: r('other') };
  }
}

type Msg = (k: string, vars?: Record<string, string>) => PlainMsg;
const withTables = (m: PlainMsg, tables: string[]): PlainMsg => ({ ...m, lists: { tables } });

/**
 * db_query: 見出しと結果の 1 行。見出しも SQL の種類に合わせる（2026-10-04 管理: DELETE なのに「書き換えますか？」は結果の「消します」と食い違う）:
 *   読む → 読みますか？ / 消す・列を消す・表を消す → 消しますか？ / 追加 → 追加しますか？ / 保存形式 → 保存形式を変更しますか？ / 書き換え・読めない → 書き換えますか？
 * ボタンの名前も同じ分け方（pendingAction.ts buttonVariant）。表の名前が取れない変更は sqlEffect が unknown にする
 */
function sqlLine(sql: unknown, h: (k: string) => PlainMsg, msg: Msg): { headline: PlainMsg; result: PlainMsg; more?: boolean } {
  const e = sqlEffect(sql);
  const unknown = { headline: h('dataChange'), result: msg('result.sqlUnknown') };
  if (e.kind === 'unknown') return unknown;
  if (e.kind === 'read') return { headline: h('dataRead'), result: msg('result.sqlRead') };
  if (e.kind === 'schema') return { headline: h('dataShape'), result: e.tables.length ? withTables(msg('result.sqlSchema'), e.tables) : msg('result.sqlSchemaAny'), more: e.more };
  if (e.tables.length === 0) return unknown;
  const key = { insert: 'sqlInsert', update: 'sqlUpdate', delete: 'sqlDelete', dropColumn: 'sqlDropColumn', drop: 'sqlDrop' }[e.kind];
  const head = e.kind === 'insert' ? 'dataAdd' : e.kind === 'update' ? 'dataChange' : 'dataDelete';
  return { headline: h(head), result: withTables(msg(`result.${key}`), e.tables), more: e.more };
}

/**
 * db_migrate: SQL（details.sql・本体がまだ渡していない）を読めれば、消える物があるかで言い分ける。読めなければ「判断できません」。
 * WHY「保存済みのデータは残ります」を既定にしないか: 読めていないのに「残ります」と言うのは、founder の言う「判断に要ること」での嘘になる
 */
function migrateLine(sql: unknown, msg: Msg): PlainMsg {
  const e = sqlEffect(sql);
  if (e.kind === 'unknown') return msg('result.sqlUnknown');
  if (e.kind === 'read' || e.kind === 'schema' || e.kind === 'insert') return msg('result.dataShapeKeep');
  if (e.tables.length === 0) return msg('result.sqlUnknown');
  if (e.kind === 'drop') return withTables(msg('result.dataShapeDrop'), e.tables);
  if (e.kind === 'dropColumn') return withTables(msg('result.dataShapeDropColumn'), e.tables);
  return withTables(msg(e.kind === 'update' ? 'result.sqlUpdate' : 'result.sqlDelete'), e.tables);
}
